import { useEffect, useLayoutEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { markDerivedStructureSet } from '~/domain/patient/derivedStructureSet';
import { isRigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSet } from '~/domain/patient/structureset';
import {
    SdfContourResolutionStrategy,
    StructureSetCopyService,
} from '~/domain/services/structureSetService/structureSetCopyService';
import { StructureSetObject } from '~/engine/data/structure/StructureSetObject';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import {
    replanningRigidStructuresState,
    replanningRigidStructuresStatusState,
    RigidStructuresStatus,
} from '~/state/atoms/adaptiveReplanningAtoms';
import { ReviewTransformOption } from './reviewTransforms';
import { useReviewDatasets } from './useReviewDatasets';
import { logErrorName } from '~/utils/logErrorName';

export interface RigidPlanningStructures {
    readonly structureSet?: StructureSet;
    readonly skippedRoiCount: number;
    readonly status?: RigidStructuresStatus;
}

// The planning structures the run propagated, moved onto the target scan by the run's rigid; built once per run.
export function useRigidPlanningStructures(
    rigid: ReviewTransformOption | undefined,
    wanted: boolean
): RigidPlanningStructures {
    const intl = useIntl();
    const patient = useSelectedPatient();
    const session = useSessionOrNull();
    const { sourceSet, propagatedSet } = useReviewDatasets();
    const [record, setRecord] = useRecoilState(replanningRigidStructuresState);
    const [status, setStatus] = useRecoilState(
        replanningRigidStructuresStatusState
    );

    const targetImage = propagatedSet?.image ?? undefined;
    const registration =
        rigid && isRigidRegistration(rigid.registration)
            ? rigid.registration
            : undefined;
    const key =
        sourceSet && propagatedSet && registration && rigid
            ? [rigid.sopInstanceUid, sourceSet.id, propagatedSet.id].join('|')
            : undefined;
    const structureSet =
        record && record.key === key
            ? patient?.structureSets.find(
                  (set) => set.dicomUid === record.structureSetUid
              )
            : undefined;

    // Held, not depended on: these return fresh values every render.
    const held = useRef({ patient, session, intl });
    useLayoutEffect(() => {
        held.current = { patient, session, intl };
    });
    const statusRef = useRef(status);
    statusRef.current = status;

    useEffect(() => {
        if (
            !wanted ||
            !key ||
            structureSet ||
            !sourceSet ||
            !propagatedSet ||
            !targetImage ||
            !registration ||
            statusRef.current?.key === key
        ) {
            return;
        }
        setStatus({ status: 'building', key });
        void (async () => {
            const sourceStructures = new StructureSetObject(sourceSet);
            try {
                const { patient: current, session: liveSession } = held.current;
                if (!current || !liveSession) {
                    throw new Error('No patient session');
                }
                const names = new Set(
                    propagatedSet.roiArray.map((roi) => roi.name)
                );
                const result =
                    await new StructureSetCopyService().copyWithRegistration(
                        {
                            sourceStructureSet: sourceSet,
                            targetImage,
                            registration,
                            existingStructureSets: current.structureSets,
                            selectedRoiNumbers: new Set(
                                sourceSet.roiArray
                                    .filter((roi) => names.has(roi.name))
                                    .map((roi) => roi.roiNumber)
                            ),
                            label: held.current.intl.formatMessage({
                                id: 'replanning.review.rigidStructures.label',
                            }),
                        },
                        new SdfContourResolutionStrategy({
                            structureSet: sourceStructures,
                        })
                    );
                markDerivedStructureSet(result.structureSet);
                liveSession.context.tracker.batch(() =>
                    current.addStructureSet(result.structureSet)
                );
                setRecord({
                    key,
                    structureSetUid: result.structureSet.dicomUid,
                    skippedRoiCount: result.skippedRois.length,
                });
                setStatus((now) => (now?.key === key ? undefined : now));
            } catch (error) {
                logErrorName(
                    'Planning structures could not be moved by the rigid:',
                    error
                );
                setStatus((now) =>
                    now?.key === key
                        ? {
                              status: 'failed',
                              key,
                              messageId:
                                  'replanning.review.rigidStructures.failed',
                          }
                        : now
                );
            } finally {
                sourceStructures.delete();
            }
        })();
    }, [
        wanted,
        key,
        structureSet,
        sourceSet,
        propagatedSet,
        targetImage,
        registration,
        setRecord,
        setStatus,
    ]);

    return {
        structureSet,
        skippedRoiCount: structureSet ? (record?.skippedRoiCount ?? 0) : 0,
        status: status?.key === key ? status : undefined,
    };
}
